"use client"

import * as React from 'react'
import { z } from 'zod'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { RailCargoType, IsoContainerSize } from '../../../../../shared/enums'

// Client-safe form schema — omits organizationId/tenantId (injected server-side)
// Imports enums from shared/enums directly to avoid pulling in @mikro-orm/core
const railCargoExtensionFormSchema = z.object({
  cargoId: z.string().uuid(),
  railCargoType: z.nativeEnum(RailCargoType).optional(),
  isoContainerSize: z.nativeEnum(IsoContainerSize).optional().nullable(),
  isoContainerTypeCode: z.string().max(4).optional().nullable(),
  intermodalUnitType: z.string().max(50).optional().nullable(),
  intermodalSscc: z.string().max(18).optional().nullable(),
  intermodalBookingRef: z.string().max(100).optional().nullable(),
  bulkMaterialCode: z.string().max(20).optional().nullable(),
  bulkLoadingMethod: z.string().max(50).optional().nullable(),
  bulkMoisturePercent: z.coerce.number().min(0).max(100).optional().nullable(),
  bulkSpecificGravity: z.coerce.number().min(0).optional().nullable(),
  wagonId: z.string().uuid().optional().nullable().or(z.literal('')),
  wagonEvn: z.string().max(12).optional().nullable(),
  loadingGauge: z.string().max(10).optional().nullable(),
  maxAxleLoadTonnes: z.coerce.number().min(0).optional().nullable(),
})

export default function CreateCargoExtensionPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const formSchema = React.useMemo(() => railCargoExtensionFormSchema, [])

  const cargoTypeOptions = React.useMemo(() => [
    { value: 'GENERAL', label: t('cim.cargoExtensions.types.GENERAL', 'General') },
    { value: 'CONTAINER', label: t('cim.cargoExtensions.types.CONTAINER', 'Container') },
    { value: 'INTERMODAL', label: t('cim.cargoExtensions.types.INTERMODAL', 'Intermodal') },
    { value: 'BULK', label: t('cim.cargoExtensions.types.BULK', 'Bulk') },
  ], [t])

  const fields = React.useMemo<CrudField[]>(() => [
    // general
    {
      id: 'cargoId',
      label: t('cim.cargoExtensions.fields.cargoId', 'Cargo ID'),
      type: 'combobox',
      required: true,
      placeholder: t('cim.cargoExtensions.placeholders.searchCargo', 'Search cargo...'),
      loadOptions: async (query?: string) => {
        const params = new URLSearchParams({ pageSize: '20' })
        if (query?.trim()) params.set('search', query.trim())
        const data = await readApiResultOrThrow<{ items?: Array<{ id: string; referenceNumber: string; cargoType: string }> }>(
          `/api/freight/cargo?${params.toString()}`,
        )
        return (data.items ?? []).map((cargo) => ({
          value: cargo.id,
          label: `${cargo.referenceNumber} (${cargo.cargoType})`,
        }))
      },
      allowCustomValues: false,
    },
    {
      id: 'railCargoType',
      label: t('cim.cargoExtensions.fields.railCargoType', 'Rail Cargo Type'),
      type: 'select',
      options: cargoTypeOptions,
    },
    // container
    {
      id: 'isoContainerSize',
      label: t('cim.cargoExtensions.fields.isoContainerSize', 'ISO Container Size'),
      type: 'select',
      options: [
        { value: '', label: '—' },
        { value: 'ISO_20', label: '20ft' },
        { value: 'ISO_40', label: '40ft' },
        { value: 'ISO_40HC', label: '40ft High Cube' },
        { value: 'ISO_45', label: '45ft' },
      ],
    },
    {
      id: 'isoContainerTypeCode',
      label: t('cim.cargoExtensions.fields.isoContainerTypeCode', 'Container Type Code'),
      type: 'text',
      placeholder: 'e.g. 22G1',
    },
    // intermodal
    {
      id: 'intermodalUnitType',
      label: t('cim.cargoExtensions.fields.intermodalUnitType', 'Intermodal Unit Type'),
      type: 'text',
      placeholder: 'e.g. swap-body, trailer',
    },
    {
      id: 'intermodalSscc',
      label: t('cim.cargoExtensions.fields.intermodalSscc', 'SSCC-18'),
      type: 'text',
      placeholder: '18-digit SSCC',
    },
    {
      id: 'intermodalBookingRef',
      label: t('cim.cargoExtensions.fields.intermodalBookingRef', 'Booking Reference'),
      type: 'text',
      placeholder: 'Optional booking reference',
    },
    // bulk
    {
      id: 'bulkMaterialCode',
      label: t('cim.cargoExtensions.fields.bulkMaterialCode', 'Bulk Material Code'),
      type: 'text',
      placeholder: 'e.g. COAL, GRAIN',
    },
    {
      id: 'bulkLoadingMethod',
      label: t('cim.cargoExtensions.fields.bulkLoadingMethod', 'Loading Method'),
      type: 'text',
      placeholder: 'e.g. gravity, pneumatic',
    },
    {
      id: 'bulkMoisturePercent',
      label: t('cim.cargoExtensions.fields.bulkMoisturePercent', 'Moisture %'),
      type: 'number',
      placeholder: '0.00',
    },
    {
      id: 'bulkSpecificGravity',
      label: t('cim.cargoExtensions.fields.bulkSpecificGravity', 'Specific Gravity'),
      type: 'number',
      placeholder: '0.0000',
    },
    // wagon
    {
      id: 'wagonId',
      label: t('cim.cargoExtensions.fields.wagonId', 'Wagon ID'),
      type: 'combobox',
      placeholder: t('cim.cargoExtensions.placeholders.searchWagon', 'Search wagons...'),
      loadOptions: async (query?: string) => {
        const params = new URLSearchParams({ pageSize: '20' })
        if (query?.trim()) params.set('evn', query.trim())
        const data = await readApiResultOrThrow<{ items?: Array<{ id: string; evn: string; name: string }> }>(
          `/api/rolling-stock/wagons?${params.toString()}`,
        )
        return (data.items ?? []).map((wagon) => ({
          value: wagon.id,
          label: wagon.name ? `${wagon.evn} — ${wagon.name}` : wagon.evn,
        }))
      },
      allowCustomValues: false,
    },
    {
      id: 'wagonEvn',
      label: t('cim.cargoExtensions.fields.wagonEvn', 'Wagon EVN'),
      type: 'text',
      placeholder: '12-digit EVN',
    },
    {
      id: 'loadingGauge',
      label: t('cim.cargoExtensions.fields.loadingGauge', 'Loading Gauge'),
      type: 'text',
      placeholder: 'e.g. GA, GB, GC',
    },
    {
      id: 'maxAxleLoadTonnes',
      label: t('cim.cargoExtensions.fields.maxAxleLoadTonnes', 'Max Axle Load (t)'),
      type: 'number',
      placeholder: '0.00',
    },
  ], [cargoTypeOptions, t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'general',
      title: t('cim.cargoExtensions.groups.general', 'General'),
      fields: ['cargoId', 'railCargoType'],
    },
    {
      id: 'container',
      title: t('cim.cargoExtensions.groups.container', 'Container'),
      fields: ['isoContainerSize', 'isoContainerTypeCode'],
    },
    {
      id: 'intermodal',
      title: t('cim.cargoExtensions.groups.intermodal', 'Intermodal'),
      fields: ['intermodalUnitType', 'intermodalSscc', 'intermodalBookingRef'],
    },
    {
      id: 'bulk',
      title: t('cim.cargoExtensions.groups.bulk', 'Bulk'),
      fields: ['bulkMaterialCode', 'bulkLoadingMethod', 'bulkMoisturePercent', 'bulkSpecificGravity'],
    },
    {
      id: 'wagon',
      title: t('cim.cargoExtensions.groups.wagon', 'Wagon'),
      fields: ['wagonId', 'wagonEvn', 'loadingGauge', 'maxAxleLoadTonnes'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('cim.cargoExtensions.create.title', 'New Rail Cargo Extension')}
          backHref="/backend/cim-cargo-extensions"
          fields={fields}
          groups={groups}
          submitLabel={t('cim.cargoExtensions.actions.create', 'New Extension')}
          cancelHref="/backend/cim-cargo-extensions"
          schema={formSchema}
          onSubmit={async (values) => {
            try {
              const payload = {
                ...values,
                organizationId,
                tenantId,
              }
              const { result: created } = await createCrud<{ id?: string }>('cim/cargo-extensions', payload)
              flash(t('cim.cargoExtensions.flash.created', 'Cargo extension created'), 'success')
              router.push('/backend/cim-cargo-extensions')
            } catch (err) {
              throw err
            }
          }}
        />
      </PageBody>
    </Page>
  )
}
